import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';

import { catchError, combineLatest, EMPTY, filter, map, Observable, of, switchMap } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  FinnairCancelFlowType,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceItem,
  FinnairTotalPricesDetails,
  ServiceFlow,
} from '@fcom/dapi/api/models';
import { ButtonTheme, ModalButtons, NotificationLayout, NotificationTheme } from '@fcom/ui-components';
import { hasUnitPrices } from '@fcom/common/utils';
import { SentryLogger } from '@fcom/core/services';
import { asPaxTypeKey } from '@fcom/dapi/utils';
import { BreakdownModel } from '@fcom/dapi/interfaces';
import { CmsArticle } from '@fcom/core-api';
import { finShare } from '@fcom/rx';
import { FragmentService } from '@fcom/common/services';

import { getPassengerServices, hasPassengers } from '../../utils/order.utils';
import { RefundDetailNotificationType } from '../../interfaces/refund.interface';
import { CancelViewModel } from '../../../cancel/interfaces';

const FLOW_TYPE_NOTIFICATION_KEYS = {
  [FinnairCancelFlowType.AUTOMATIC_RULE_24_HOUR]: 'manualAndAutomaticRule24Hour',
  [FinnairCancelFlowType.AUTOMATIC_RULE_TICKET_SLIGHTCLASSIC]: 'manualAndAutomaticRuleSlightClassic',
  [FinnairCancelFlowType.AUTOMATIC_RULE_TICKET_FLEX]: 'manualAndAutomaticRuleTicketFlex',
  [FinnairCancelFlowType.AUTOMATIC_RULE_TICKET_OTHER]: 'manualAndAutomaticTicketOther',
  [FinnairCancelFlowType.MANUAL_RULE_24_HOUR]: 'manualAndAutomaticRule24Hour',
  [FinnairCancelFlowType.MANUAL_RULE_TICKET_OTHER]: 'manualAndAutomaticTicketOther',
  [FinnairCancelFlowType.MANUAL_RULE_TICKET_FLEX]: 'manualAndAutomaticRuleTicketFlex',
  [FinnairCancelFlowType.MANUAL_RULE_TICKET_SLIGHTCLASSIC]: 'manualAndAutomaticRuleSlightClassic',
  [FinnairCancelFlowType.MANUAL_RULE_TICKET_AWARD]: 'manualAndAutomaticTicketAward',
  [FinnairCancelFlowType.RULE_CACR]: 'cacr',
  [FinnairCancelFlowType.MANUAL_INVOLUNTARY]: 'manualAndAutomaticInvoluntary',
  [FinnairCancelFlowType.AUTOMATIC_INVOLUNTARY]: 'manualAndAutomaticInvoluntary',
};

const DEPRECATED_AND_INVOLUNTARY_RULES = [
  FinnairCancelFlowType.MANUAL_INVOLUNTARY,
  FinnairCancelFlowType.AUTOMATIC_INVOLUNTARY,
];

const NON_REFUNDABLE_EMD_FLOW_TYPES: FinnairCancelFlowType[] = [
  FinnairCancelFlowType.AUTOMATIC_RULE_TICKET_SLIGHTCLASSIC,
  FinnairCancelFlowType.MANUAL_RULE_TICKET_SLIGHTCLASSIC,
  FinnairCancelFlowType.RULE_CACR,
  FinnairCancelFlowType.MANUAL_RULE_TICKET_AWARD,
];

const KOREA_COUNTRY_CODE = 'KR';

@Component({
  selector: 'fin-refund-detail',
  templateUrl: './refund-detail.component.html',
  styleUrls: ['./refund-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RefundDetailComponent implements OnInit {
  private sentryLogger = inject(SentryLogger);
  private fragmentService = inject(FragmentService);

  readonly asPaxTypeKey = asPaxTypeKey;
  readonly ModalButtons = ModalButtons;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly ServiceFlow = ServiceFlow;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;

  @Input({ required: true })
  orderViewModel$: Observable<CancelViewModel>;

  @Input({ required: true })
  notificationType: RefundDetailNotificationType;

  @Output()
  goToClaimForm: EventEmitter<void> = new EventEmitter<void>();

  mainNotificationContent$: Observable<CmsArticle>;
  flightTicketNotificationKey$: Observable<string>;
  travelExtrasNotificationKey$: Observable<string>;
  displayFlightTicketNotification$: Observable<boolean>;
  displayRefundNotificationWithLink$: Observable<boolean>;
  displayMainNotification$: Observable<boolean>;
  displayGoToClaimFormButton$: Observable<boolean>;
  displayRefundAmountBlock$: Observable<boolean>;
  displayEligibleForRefundMessage$: Observable<boolean>;
  isDeprecatedOrInvoluntaryRule$: Observable<boolean>;
  travelExtraRefundInfo$: Observable<{
    hasServices: boolean;
    isNonRefundable: boolean;
  }>;
  services$: Observable<FinnairServiceItem[]>;

  summaryModalOpen: boolean;
  summaryModalData: { prices: FinnairTotalPricesDetails };
  breakdowns$: Observable<BreakdownModel[]> = of([]);

  ngOnInit(): void {
    this.services$ = this.orderViewModel$.pipe(map((orderViewModel) => orderViewModel.paidServices));
    this.isDeprecatedOrInvoluntaryRule$ = this.orderViewModel$.pipe(
      map((orderViewModel) => DEPRECATED_AND_INVOLUNTARY_RULES.includes(orderViewModel.flowType)),
      finShare()
    );
    this.breakdowns$ = this.orderViewModel$.pipe(
      filter(
        (orderViewModel) => hasPassengers(orderViewModel.passengers) && hasUnitPrices(orderViewModel.refundAmount, true)
      ),
      map((orderViewModel) => {
        const refundAmount = orderViewModel.refundAmount;
        return orderViewModel.passengers.map((passenger: FinnairPassengerItem) => ({
          ...passenger,
          prices:
            refundAmount.flight?.totalPerPax?.[passenger.id] || refundAmount.services?.totalPerPax?.[passenger.id],
          services: getPassengerServices(refundAmount.services?.totalPerCategory, passenger),
          total: refundAmount.total.totalPerPax?.[passenger.id]?.totalAmount,
        }));
      }),
      catchError((e: unknown) => {
        this.sentryLogger.error('Exception in handling prices of passengers', e);
        return EMPTY;
      })
    );
    this.displayGoToClaimFormButton$ = this.orderViewModel$.pipe(
      map(
        (orderViewModel) =>
          orderViewModel.flowType === FinnairCancelFlowType.RULE_CACR &&
          this.notificationType === RefundDetailNotificationType.CONFIRMATION
      )
    );
    this.mainNotificationContent$ = this.orderViewModel$.pipe(
      map((orderViewModel) => this.getMainNotificationKey(orderViewModel.flowType, orderViewModel.order)),
      switchMap((key) => this.fragmentService.getFragment<CmsArticle>(key)),
      finShare()
    );
    this.displayFlightTicketNotification$ = combineLatest([
      this.orderViewModel$,
      this.isDeprecatedOrInvoluntaryRule$,
      this.services$,
    ]).pipe(
      map(([orderViewModel, isDeprecatedOrInvoluntary, services]) => {
        return isDeprecatedOrInvoluntary
          ? this.notificationType === RefundDetailNotificationType.REVIEW || !!services?.length
          : orderViewModel.flowType !== FinnairCancelFlowType.MANUAL_RULE_24_HOUR &&
              orderViewModel.flowType !== FinnairCancelFlowType.AUTOMATIC_RULE_24_HOUR;
      })
    );
    this.flightTicketNotificationKey$ = this.displayFlightTicketNotification$.pipe(
      filter((displayNotification) => displayNotification),
      switchMap(() =>
        this.orderViewModel$.pipe(
          map((orderViewModel) => this.getFlightTicketNotificationKey(orderViewModel.flowType)),
          finShare()
        )
      )
    );
    this.displayRefundNotificationWithLink$ = combineLatest([
      this.displayFlightTicketNotification$,
      this.isDeprecatedOrInvoluntaryRule$,
    ]).pipe(
      map(
        ([displayFlightNotification, isDeprecatedOrInvoluntary]) =>
          !displayFlightNotification &&
          isDeprecatedOrInvoluntary &&
          this.notificationType === RefundDetailNotificationType.CONFIRMATION
      )
    );
    this.travelExtrasNotificationKey$ = this.orderViewModel$.pipe(
      map((orderViewModel) => this.getTravelExtrasNotificationKey(orderViewModel.flowType)),
      finShare()
    );
    this.displayMainNotification$ = combineLatest([this.isDeprecatedOrInvoluntaryRule$, this.services$]).pipe(
      map(
        ([isDeprecatedOrInvoluntary, services]) =>
          !isDeprecatedOrInvoluntary ||
          (services?.length && this.notificationType === RefundDetailNotificationType.CONFIRMATION)
      )
    );
    this.displayEligibleForRefundMessage$ = this.orderViewModel$.pipe(
      map((orderViewModel) => this.displayEligibleForRefundMessage(orderViewModel.flowType))
    );
    this.displayRefundAmountBlock$ = this.orderViewModel$.pipe(
      map(
        (orderViewModel) =>
          orderViewModel.flowType !== FinnairCancelFlowType.MANUAL_RULE_TICKET_AWARD && !!orderViewModel.order
      )
    );
    this.travelExtraRefundInfo$ = combineLatest([this.orderViewModel$, this.services$]).pipe(
      map(([orderViewModel, services]) => ({
        hasServices: services?.length > 0,
        isNonRefundable: NON_REFUNDABLE_EMD_FLOW_TYPES.includes(orderViewModel.flowType),
      })),
      finShare()
    );
  }

  openSummaryModal = (event: Event, prices: FinnairTotalPricesDetails): void => {
    event.stopPropagation();

    this.summaryModalOpen = true;
    this.summaryModalData = {
      prices,
    };
  };

  closeSummaryModal = (): void => {
    this.summaryModalData = null;
  };

  onGoToClaimForm(): void {
    this.goToClaimForm.emit();
  }

  private getMainNotificationKey(flow: FinnairCancelFlowType, order: FinnairOrder): string {
    if (this.is24HourKoreaRule(order, flow)) {
      return `MMB.refund.rules.koreaRule24Hour.mainNotification.${this.notificationType}.url`;
    }
    return `MMB.refund.rules.${FLOW_TYPE_NOTIFICATION_KEYS[flow]}.mainNotification.${this.notificationType}.url`;
  }

  private is24HourKoreaRule(order: FinnairOrder, flowType: FinnairCancelFlowType): boolean {
    const is24hRuleFlow =
      flowType === FinnairCancelFlowType.AUTOMATIC_RULE_24_HOUR ||
      flowType === FinnairCancelFlowType.MANUAL_RULE_24_HOUR;

    const departureLocationCode = order.bounds[0]?.departure?.locationCode;
    const locationData = order.locations?.[departureLocationCode];
    const isDepartureFromKorea = locationData?.countryCode === KOREA_COUNTRY_CODE;

    return is24hRuleFlow && isDepartureFromKorea;
  }

  private getFlightTicketNotificationKey(flow: FinnairCancelFlowType): string {
    return `MMB.refund.rules.${FLOW_TYPE_NOTIFICATION_KEYS[flow]}.flightTicketsNotification.${this.notificationType}`;
  }

  private getTravelExtrasNotificationKey(flow: FinnairCancelFlowType): string {
    return `MMB.refund.rules.${FLOW_TYPE_NOTIFICATION_KEYS[flow]}.travelExtrasNotification.${this.notificationType}`;
  }

  private displayEligibleForRefundMessage(flow: FinnairCancelFlowType): boolean {
    return flow === FinnairCancelFlowType.AUTOMATIC_RULE_24_HOUR || flow === FinnairCancelFlowType.MANUAL_RULE_24_HOUR;
  }
}
